import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { MoreVertical } from "react-feather";
import { UserListTableData } from "../../../data/users/UsersList";
import { showWarningAlert } from "../../../utils/showWarningAlert";
import CommonModal from "../../modal/common/CommonModal";
import UserResetPassword from "../../modal/common/user-reset-password/UserResetPassword";
 import { Badges, H3, H5, Image, SVG } from "../../../AbstractElements";
import { dynamicImage } from "../../../service";
import InviteSignupModal from "../../common/InviteSignupModal";
 import { UserListTableDataColumnType } from "../../../types/users/UserTypes";
import { useRoles } from "../../../context/RolesContext";
import FilterSearchSection from "../../common/filter-section/FilterSearchSection";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";

function UserListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useParams<{ role: string }>();
  const { roles, isLoading } = useRoles();
  const [visibleCount, setVisibleCount] = useState(8);
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    if (!isLoading && (!role || !roles.includes(role.toLowerCase()))) {
      navigate(`${process.env.PUBLIC_URL}/error`, { replace: true });
    }
  }, [role, roles, isLoading, navigate]);

  const doubledData = [...UserListTableData, ...UserListTableData];

  const handleScroll = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 100
    ) {
      setVisibleCount((prev) => Math.min(prev + 2, doubledData.length));
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleAddUser = () => {
    navigate(`${process.env.PUBLIC_URL}/users/${role}/new`);
  };

  const handleInvite = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
  };

  const UserListTableAction: React.FC<{ row: UserListTableDataColumnType }> = ({
    row,
  }) => {
    const [selectedUser, setSelectedUser] = useState<UserListTableDataColumnType | null>(
      null
    );
    const [staticModal, setStaticModal] = useState(false);

    const staticModalToggle = () => {
      setStaticModal(!staticModal);
      setSelectedUser(row);
    };

    const handleBlock = () => {
      showWarningAlert({
        title: t("Are you sure?"),
        text: `Do you want to block ${row.name}?`,
        confirmButtonText: t("Block"),
        onConfirm: () => {},
      });
    };

    const handleUnblock = () => {
      showWarningAlert({
        title: t("Are you sure?"),
        text: `Do you want to unblock ${row.name}?`,
        confirmButtonText: t("Unblock"),
        onConfirm: () => {},
      });
    };

    return (
      <UncontrolledDropdown>
        <DropdownToggle tag="span" className="cursor-pointer">
          <MoreVertical size={18} />
        </DropdownToggle>
        <DropdownMenu>
          <Link to={`${process.env.PUBLIC_URL}/users/${role}/view/${row.id}`}>
            <DropdownItem>{t("View")}</DropdownItem>
          </Link>
          <Link to={`${process.env.PUBLIC_URL}/users/${role}/edit/${row.id}`}>
            <DropdownItem>{t("Edit")}</DropdownItem>
          </Link>
          {row.status === "Active" ? (
            <DropdownItem onClick={handleBlock}>{t("Block")}</DropdownItem>
          ) : (
            <DropdownItem onClick={handleUnblock}>{t("Unblock")}</DropdownItem>
          )}
          <DropdownItem onClick={staticModalToggle}>
            {t("ResetPassword")}
          </DropdownItem>
          <Link
            to={`${process.env.PUBLIC_URL}/users/${role}/activity-logs/${row.id}`}
          >
            <DropdownItem>{t("ActivityLog")}</DropdownItem>
          </Link>
          <Link
            to={`${process.env.PUBLIC_URL}/users/${role}/user-access-rights/${row.id}`}
          >
            <DropdownItem>{t("AccessRights")}</DropdownItem>
          </Link>
        </DropdownMenu>
        <CommonModal
          backdrop="static"
          modalBodyClassName="social-profile text-start"
          isOpen={staticModal}
          toggle={staticModalToggle}
        >
          <H3 className="modal-header justify-content-center border-0">
            <span className="font-primary email_add_0">
              {selectedUser?.organizationname}
            </span>
          </H3>
          <div className="modal-toggle-wrapper">
            <UserResetPassword
              staticModalToggle={staticModalToggle}
              data={selectedUser}
            />
          </div>
        </CommonModal>
      </UncontrolledDropdown>
    );
  };

  const getRoleTranslation = (role: string | undefined) => {
    if (!role) return t("Users");
    const formattedRole = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    const translated = t(formattedRole);
    return translated === formattedRole ? formattedRole : translated;
  };


  if (isLoading) {
    return <div>{t("Loading roles...")}</div>;
  }

  return (
    <>
      <Breadcrumbs
        mainTitle={getRoleTranslation(role)}
        parent={t("Users")}
      />
      <Container fluid>
        <FilterSearchSection
          hideOrganization={true}
          showUserActions={true}
          onAddUser={handleAddUser}
          onInvite={handleInvite}
        />
        <Row>
          {doubledData.slice(0, visibleCount).map((item, index) => (
            <Col
              xl="4"
              sm="6"
              xxl="3"
              className="col-ed-4 box-col-4"
              key={`${item.id}-${index}`}
            >
              <Card className="social-profile">
                <CardBody>
                  <div className="social-img-wrap">
                    <div className="social-img">
                      <Image src={dynamicImage(item.image)} alt="user" />
                    </div>
                    <div className="edit-icon">
                      <SVG iconId="profile-check" />
                    </div>
                  </div>
                  <div style={{ position: "absolute", top: 10, right: 10 }}>
                    <UserListTableAction row={item} />
                  </div>
                  <div className="social-details">
                    <H5 className="mb-1">
                      <Link to={`${process.env.PUBLIC_URL}/users/${role}/view/${item.id}`}>{item.name}</Link>
                    </H5>
                  </div>
                  <div className="social-details">
                    <H3 className="mb-1">
                      <Link to="">{item.organizationname}</Link>
                    </H3>
                  </div>
                  <Badges
                    color="transparent"
                    className={`badge-light-${
                      item.status === "Inactive" ? "secondary" : "success"
                    }`}
                  >
                    {item.status}
                  </Badges>
                  <hr />
                  <div className="social-details">
                    <H5 className="mb-1 d-flex align-items-center gap-2">
                      <i className="fa fa-phone" />
                      <Link to="">{item.phonenumber}</Link>
                    </H5>
                    <H5 className="mb-1 d-flex align-items-center gap-2">
                      <i className="fa fa-envelope" />
                      <span className="f-light"> {item.email}</span>
                    </H5>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      <InviteSignupModal
        isOpen={showInviteModal}
        onClose={handleCloseInviteModal}
      />
    </>
  );
}

export default UserListPage;